import { unembed } from '@blackbird/common/embedding';
import * as Schema from 'generated/graphql/schema';

import { useUtils } from '@/hocs/with-utils';

const sortPriority: Record<keyof Schema.ActionMap, number> = {
  make: 4,
  take: 3,
  pending: 2,
  resolve: 1,
};

export const buildSummary = (action: Schema.ActionMap, utils: ReturnType<typeof useUtils>) => {
  const actions = Object.entries(action).filter(([, a]) => !!a) as [keyof Schema.ActionMap, Schema.Action][];

  actions.sort(([actionLeft], [actionRight]) => {
    return sortPriority[actionRight] - sortPriority[actionLeft];
  });

  const [summary] = actions.reduce(
    ([summary, previousCallTime], [step, { author, details, time }]) => {
      if (typeof details !== 'string') {
        return [summary, previousCallTime];
      }

      const callTime = new Date(time);

      const timeSinceLastCall = previousCallTime ? <b> ({utils.formatDistance(previousCallTime, callTime)})</b> : null;

      const [unembeddedDetails] = unembed(details);
      const formattedDetails = unembeddedDetails.split('\n').reduce((d, part) => {
        return (
          <>
            {d}
            {part}
            <br />
          </>
        );
      }, <></>);

      const quotedDetails = step === 'take' ? <br /> : <blockquote>{formattedDetails}</blockquote>;

      return [
        <>
          {summary}
          <code>{author || 'N/A'} </code> at {[callTime.toLocaleDateString(), callTime.toLocaleTimeString()].join(', ')}
          {timeSinceLastCall}
          {quotedDetails}
        </>,
        callTime,
      ];
    },
    [<></>, null] as [JSX.Element, null | Date],
  );

  return summary;
};

export const formatTimestamp = (time: string | undefined) => {
  if (!time) {
    return '';
  }

  const date = new Date(time);

  const localeDateString = date.toLocaleDateString();
  const localeTimeString = date.toLocaleTimeString();

  return [localeDateString, localeTimeString].join(', ');
};
